import { useState } from 'react';
import { View, Text, TextInput, Pressable, Modal, Platform, ActivityIndicator, Keyboard } from 'react-native';
import { KeyboardAwareBody } from '@/components/modal/KeyboardAwareBody';
import { useKeyboardVisible } from '@/lib/useKeyboardVisible';
import { useMutation } from '@tanstack/react-query';
import Animated, { FadeIn, FadeInUp, SlideInDown } from 'react-native-reanimated';
import { X, Mail, Lock, Eye, EyeOff, Linkedin, User } from 'lucide-react-native';
import { useAuth, isSupabaseConfigured, supabase } from '@/lib/supabase';
import { useRouter } from 'expo-router';
import { useProfileStore } from '@/lib/state/profile-store';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import { handleError } from '@/lib/errors';
import { isBetaMode } from '@/lib/config/app-mode';
import type { Profile } from '@/lib/supabase';

const MODAL_COLORS = {
  dark: {
    bgOverlay: 'rgba(0,0,0,0.6)',
    bgModal: '#0f1f38',
    bgInput: 'rgba(255,255,255,0.06)',
    border: 'rgba(255,255,255,0.1)',
    borderLight: 'rgba(255,255,255,0.15)',
    teal: '#4fd1c5',
    tealDim: 'rgba(79,209,197,0.15)',
    white: '#FFFFFF',
    text1: 'rgba(255,255,255,0.85)',
    text2: 'rgba(255,255,255,0.6)',
    text3: 'rgba(255,255,255,0.38)',
    error: '#f87171',
    linkedin: '#0A66C2',
    submitText: '#0a1628',
    submitBgDisabled: 'rgba(79,209,197,0.4)',
  },
  light: {
    bgOverlay: 'rgba(0,0,0,0.35)',
    bgModal: '#ffffff',
    bgInput: '#f5f5f7',
    border: 'rgba(0,0,0,0.10)',
    borderLight: 'rgba(0,0,0,0.15)',
    teal: '#0d7d71',
    tealDim: 'rgba(13,125,113,0.10)',
    white: '#1d1d1f',
    text1: '#1d1d1f',
    text2: '#48484a',
    text3: '#8e8e93',
    error: '#dc2626',
    linkedin: '#0A66C2',
    submitText: '#ffffff',
    submitBgDisabled: 'rgba(13,125,113,0.4)',
  },
};

function sanitizeBetaAuthError(msg: string): string {
  const lower = msg.toLowerCase();
  if (lower.includes('invite-only') || lower.includes('waitlist') || lower.includes('p0403')) {
    return msg.includes("haven't been invited")
      ? msg
      : 'Registration is invite-only during the beta. Join the waitlist first.';
  }
  if (lower.includes('invalid login credentials') || lower.includes('invalid password')) {
    return 'Incorrect email or password.';
  }
  if (lower.includes('user already registered') || lower.includes('already exists')) {
    return 'An account with this email already exists. Try signing in.';
  }
  if (lower.includes('email not confirmed')) {
    return 'Please check your email to confirm your account.';
  }
  return 'Authentication failed. Please try again.';
}

interface BetaLoginModalProps {
  visible: boolean;
  onClose: () => void;
  colorScheme?: 'dark' | 'light';
}

export function BetaLoginModal({ visible, onClose, colorScheme = 'dark' }: BetaLoginModalProps) {
  const C = MODAL_COLORS[colorScheme];
  const keyboardVisible = useKeyboardVisible();
  const router = useRouter();
  const { signIn, signUp, signInWithLinkedIn, isConfigured } = useAuth();
  const setUserRole = useProfileStore((s) => s.setUserRole);
  const setPendingAuthRole = useProfileStore((s) => s.setPendingAuthRole);
  const setUser = useProfileStore((s) => s.setUser);
  const setSubscriptionUserRole = useSubscriptionStore((s) => s.setUserRole);

  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedRole, setSelectedRole] = useState<'startup' | 'investor' | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const resetForm = () => {
    setEmail('');
    setFirstName('');
    setLastName('');
    setPassword('');
    setShowPassword(false);
    setSelectedRole(null);
    setError('');
    setSuccess('');
    setMode('login');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const submitMutation = useMutation({
    mutationFn: async () => {
      setError('');

      try {
        if (mode === 'signup') {
          setPendingAuthRole(selectedRole!);
          const { error: authError, needsEmailConfirmation } = await signUp(email, password, selectedRole!, firstName, lastName);
          if (authError) {
            setError(sanitizeBetaAuthError(authError.message));
            return;
          }
          if (needsEmailConfirmation) {
            setSuccess('Check your email for a confirmation link, then come back to sign in.');
            setMode('login');
            return;
          }
          handleClose();
          router.replace(`/(onboarding)/${selectedRole}-welcome`);
        } else {
          const { error: authError, mfaRequired, mfaChallenge } = await signIn(email, password);
          if (authError) {
            setError(sanitizeBetaAuthError(authError.message));
            return;
          }
          if (mfaRequired && mfaChallenge) {
            handleClose();
            router.push({
              pathname: '/mfa-verify',
              params: {
                factorId: mfaChallenge.factorId,
                challengeId: mfaChallenge.challengeId,
              },
            });
            return;
          }
          const { data: { user: authUser } } = await supabase.auth.getUser();
          if (authUser) {
            const { data: dbProfile } = await supabase
              .from('profiles')
              .select('role')
              .eq('id', authUser.id)
              .single() as { data: Profile | null };

            const dbRole = (dbProfile?.role as 'startup' | 'investor' | 'admin') || 'startup';

            if (dbRole === 'admin') {
              setUserRole('admin');
              setSubscriptionUserRole('admin');
              handleClose();
              router.replace('/(admin)/admin-dashboard');
              return;
            }

            setUserRole(dbRole);
            setSubscriptionUserRole(dbRole);
            handleClose();
            router.replace(dbRole === 'startup' ? '/(startup)/startup-dashboard' : '/(investor)/investor-dashboard');
          }
        }
      } catch (err) {
        const { message, isCancelled } = handleError(err, { context: 'BetaLoginModal.handleSubmit' });
        if (!isCancelled) setError(message);
      }
    },
  });

  const linkedInMutation = useMutation({
    mutationFn: async () => {
      setError('');

      if (selectedRole) {
        setPendingAuthRole(selectedRole);
        if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('pendingAuthRole', selectedRole);
        }
      }

      try {
        const { error: authError } = await signInWithLinkedIn();
        if (authError) {
          setError(sanitizeBetaAuthError(authError.message));
        }
      } catch (err) {
        const { message, isCancelled } = handleError(err, { context: 'BetaLoginModal.linkedin' });
        if (!isCancelled) setError(message);
      }
    },
  });

  const isLoading = submitMutation.isPending || linkedInMutation.isPending;

  const handleSubmit = () => {
    if (!email.trim() || !password.trim()) {
      setError('Please enter your email and password.');
      return;
    }

    if (mode === 'signup' && !selectedRole) {
      setError('Please select your role.');
      return;
    }

    if (mode === 'signup' && (!firstName.trim() || !lastName.trim())) {
      setError('Please enter your first and last name.');
      return;
    }

    submitMutation.mutate();
  };

  const handleLinkedIn = () => {
    if (mode === 'signup' && !selectedRole) {
      setError('Please select your role first.');
      return;
    }

    linkedInMutation.mutate();
  };

  return (
    <Modal visible={visible} transparent animationType="none" onRequestClose={handleClose}>
      <Animated.View
        entering={FadeIn.duration(200)}
        style={{ flex: 1, backgroundColor: C.bgOverlay, justifyContent: 'center', alignItems: 'center', padding: 20 }}
      >
        {/* Backdrop: while the keyboard is up the first tap puts it away and
            keeps the form; only a second tap closes. Closing on the first tap
            threw away a half-typed email on the app's own sign-in surface. */}
        <Pressable
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
          accessibilityLabel={keyboardVisible ? 'Dismiss keyboard' : 'Close'}
          onPress={() => {
            if (keyboardVisible) {
              Keyboard.dismiss();
              return;
            }
            handleClose();
          }}
        />

        <Animated.View
          entering={SlideInDown.duration(350).springify()}
          style={{
            width: '100%',
            maxWidth: 420,
            backgroundColor: C.bgModal,
            borderRadius: 20,
            borderWidth: 1,
            borderColor: C.border,
            // `padding` and `gap` move to the scroll content container below so
            // the card keeps its exact look while the body can scroll.
            flexShrink: 1,
          }}
        >
        {/* Keyboard-aware body — scrolls the focused field into view. The card
            above keeps every pixel of its bespoke dark styling; only the inner
            scroll container changed. See components/modal/KeyboardAwareBody. */}
        <KeyboardAwareBody contentContainerStyle={{ padding: 32, gap: 20 }}>
          {/* Header */}
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={{ color: C.white, fontSize: 22, fontWeight: '700' }}>
              {mode === 'login' ? 'Sign In' : 'Create Account'}
            </Text>
            <Pressable onPress={handleClose} style={{ padding: 4 }}>
              <X size={20} color={C.text3} />
            </Pressable>
          </View>

          {isBetaMode() && mode === 'signup' && (
            <View style={{ backgroundColor: C.tealDim, borderRadius: 10, padding: 10 }}>
              <Text style={{ color: C.teal, fontSize: 12, textAlign: 'center' }}>
                Beta access is invite-only. You must be on the waitlist with an invite.
              </Text>
            </View>
          )}

          {/* Role selection (signup only) */}
          {mode === 'signup' && (
            <View style={{ flexDirection: 'row', gap: 12 }}>
              {(['startup', 'investor'] as const).map((r) => (
                <Pressable
                  key={r}
                  onPress={() => setSelectedRole(r)}
                  style={{
                    flex: 1,
                    paddingVertical: 14,
                    borderRadius: 12,
                    alignItems: 'center',
                    borderWidth: 1.5,
                    borderColor: selectedRole === r
                      ? (r === 'startup' ? C.teal : '#818cf8')
                      : C.border,
                    backgroundColor: selectedRole === r
                      ? (r === 'startup' ? C.tealDim : 'rgba(129,140,248,0.12)')
                      : C.bgInput,
                  }}
                >
                  <Text style={{
                    color: selectedRole === r
                      ? (r === 'startup' ? C.teal : '#818cf8')
                      : C.text2,
                    fontSize: 14,
                    fontWeight: '600',
                  }}>
                    {r === 'startup' ? 'Startup' : 'Investor'}
                  </Text>
                </Pressable>
              ))}
            </View>
          )}

          {/* LinkedIn */}
          <Pressable
            onPress={handleLinkedIn}
            disabled={isLoading}
            style={{
              backgroundColor: C.linkedin,
              borderRadius: 12,
              paddingVertical: 14,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              opacity: isLoading ? 0.6 : 1,
            }}
          >
            <Linkedin size={18} color="#fff" />
            <Text style={{ color: '#fff', fontWeight: '600', fontSize: 14 }}>
              Continue with LinkedIn
            </Text>
          </Pressable>

          {/* Divider */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <View style={{ flex: 1, height: 1, backgroundColor: C.border }} />
            <Text style={{ color: C.text3, fontSize: 12 }}>or</Text>
            <View style={{ flex: 1, height: 1, backgroundColor: C.border }} />
          </View>

          {/* Name (signup only) — flows to profiles.first_name/last_name via the
              handle_new_user trigger so onboarding step 1 prefills. */}
          {mode === 'signup' && (
            <View style={{ flexDirection: 'row', gap: 12 }}>
              <View style={{
                flex: 1, flexDirection: 'row', alignItems: 'center', gap: 10,
                backgroundColor: C.bgInput, borderRadius: 12, borderWidth: 1, borderColor: C.border,
                paddingHorizontal: 14,
              }}>
                <User size={16} color={C.text3} />
                <TextInput
                  value={firstName}
                  onChangeText={(t) => { setFirstName(t); setError(''); }}
                  placeholder="First name"
                  placeholderTextColor={C.text3}
                  autoCapitalize="words"
                  autoCorrect={false}
                  style={{ flex: 1, color: C.white, fontSize: 14, paddingVertical: 14 }}
                />
              </View>
              <View style={{
                flex: 1, flexDirection: 'row', alignItems: 'center', gap: 10,
                backgroundColor: C.bgInput, borderRadius: 12, borderWidth: 1, borderColor: C.border,
                paddingHorizontal: 14,
              }}>
                <TextInput
                  value={lastName}
                  onChangeText={(t) => { setLastName(t); setError(''); }}
                  placeholder="Last name"
                  placeholderTextColor={C.text3}
                  autoCapitalize="words"
                  autoCorrect={false}
                  style={{ flex: 1, color: C.white, fontSize: 14, paddingVertical: 14 }}
                />
              </View>
            </View>
          )}

          {/* Email */}
          <View style={{
            flexDirection: 'row', alignItems: 'center', gap: 10,
            backgroundColor: C.bgInput, borderRadius: 12, borderWidth: 1, borderColor: C.border,
            paddingHorizontal: 14,
          }}>
            <Mail size={16} color={C.text3} />
            <TextInput
              value={email}
              onChangeText={(t) => { setEmail(t); setError(''); }}
              placeholder="your@email.com"
              placeholderTextColor={C.text3}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              style={{ flex: 1, color: C.white, fontSize: 14, paddingVertical: 14 }}
            />
          </View>

          {/* Password */}
          <View style={{
            flexDirection: 'row', alignItems: 'center', gap: 10,
            backgroundColor: C.bgInput, borderRadius: 12, borderWidth: 1, borderColor: C.border,
            paddingHorizontal: 14,
          }}>
            <Lock size={16} color={C.text3} />
            <TextInput
              value={password}
              onChangeText={(t) => { setPassword(t); setError(''); }}
              placeholder="Password"
              placeholderTextColor={C.text3}
              secureTextEntry={!showPassword}
              autoCapitalize="none"
              style={{ flex: 1, color: C.white, fontSize: 14, paddingVertical: 14 }}
            />
            <Pressable onPress={() => setShowPassword(!showPassword)}>
              {showPassword ? <EyeOff size={16} color={C.text3} /> : <Eye size={16} color={C.text3} />}
            </Pressable>
          </View>

          {/* Error / Success */}
          {error ? (
            <Animated.View entering={FadeInUp.duration(200)}>
              <Text style={{ color: C.error, fontSize: 13, textAlign: 'center' }}>{error}</Text>
            </Animated.View>
          ) : null}
          {success ? (
            <Animated.View entering={FadeInUp.duration(200)}>
              <Text style={{ color: C.teal, fontSize: 13, textAlign: 'center' }}>{success}</Text>
            </Animated.View>
          ) : null}

          {/* Submit */}
          <Pressable
            onPress={handleSubmit}
            disabled={isLoading}
            style={{
              backgroundColor: isLoading ? C.submitBgDisabled : C.teal,
              borderRadius: 12,
              paddingVertical: 14,
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'row',
              gap: 8,
            }}
          >
            {isLoading && <ActivityIndicator size="small" color={C.submitText} />}
            <Text style={{ color: C.submitText, fontWeight: '700', fontSize: 15 }}>
              {isLoading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
            </Text>
          </Pressable>

          {/* Toggle mode */}
          <Pressable onPress={() => { setMode(mode === 'login' ? 'signup' : 'login'); setError(''); setSuccess(''); }}>
            <Text style={{ color: C.text2, fontSize: 13, textAlign: 'center' }}>
              {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
              <Text style={{ color: C.teal, fontWeight: '600' }}>
                {mode === 'login' ? 'Sign Up' : 'Sign In'}
              </Text>
            </Text>
          </Pressable>
        </KeyboardAwareBody>
        </Animated.View>
      </Animated.View>
    </Modal>
  );
}
